/**
 * ROUTE MAP VIEW - GPS Trip Visualization
 * 
 * Interactive map showing GPS routes with:
 * - Polyline route path
 * - Start/end markers
 * - Stop duration markers
 * - Timestamp tooltips
 * - Speed indicators
 * 
 * Uses Leaflet (free, no API key required)
 */

import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background-color: ${color};
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const startIcon = createIcon('#10b981');  // Green
const endIcon = createIcon('#ef4444');    // Red
const stopIcon = createIcon('#f59e0b');   // Amber

// Component to fit map bounds to route
const FitBounds = ({ points }) => {
  const map = useMap();
  
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, points]);
  
  return null;
};

// Detect stops (low speed or stationary points)
const detectStops = (points, minDurationMinutes = 5) => {
  const stops = [];
  let stopStart = null;
  
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const isStationary = !point.speed || point.speed < 2; // km/h
    
    if (isStationary && !stopStart) {
      stopStart = { index: i, point };
    } else if (!isStationary && stopStart) {
      const duration = (new Date(point.timestamp) - new Date(stopStart.point.timestamp)) / 60000;
      if (duration >= minDurationMinutes) {
        stops.push({
          ...stopStart.point,
          duration: Math.round(duration),
          endIndex: i
        });
      }
      stopStart = null;
    }
  }
  
  return stops;
};

// Format duration
const formatDuration = (minutes) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Format time
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const RouteMapView = ({ 
  routePoints = [], 
  date = '',
  totalMiles = 0,
  onClose,
  employeeName = ''
}) => {
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);

  // Calculate stops
  const stops = detectStops(routePoints);

  // Get start and end points
  const startPoint = routePoints[0];
  const endPoint = routePoints[routePoints.length - 1];

  // Create route polyline coordinates
  const routePath = routePoints.map(p => [p.lat, p.lng]);

  // Calculate average speed
  const avgSpeed = routePoints.filter(p => p.speed).length > 0
    ? (routePoints.filter(p => p.speed).reduce((sum, p) => sum + (p.speed || 0), 0) / routePoints.filter(p => p.speed).length).toFixed(1)
    : null;

  // Calculate trip duration
  const tripDuration = startPoint && endPoint && startPoint.timestamp && endPoint.timestamp
    ? Math.round((new Date(endPoint.timestamp) - new Date(startPoint.timestamp)) / 60000)
    : null;

  if (routePoints.length === 0) {
    return (
      <div style={{
        backgroundColor: '#0f172a',
        borderLeft: '3px solid #f59e0b',
        borderRadius: '8px',
        padding: '32px',
        textAlign: 'center',
      }}>
        <div style={{ color: '#f59e0b', fontWeight: 'bold', marginBottom: '8px' }}>
          NO ROUTE DATA
        </div>
        <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
          No GPS points recorded for {date || 'this date'}
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            marginTop: '16px',
            padding: '8px 24px',
            borderRadius: '6px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            cursor: 'pointer',
          }}>
            CLOSE
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#0f172a',
      borderLeft: '3px solid #10b981',
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', letterSpacing: '0.05em' }}>
            ROUTE MAP {employeeName && `- ${employeeName}`}
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '4px' }}>
            {date}
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.75rem',
          }}>
            CLOSE
          </button>
        )}
      </div>

      {/* Stats Bar */}
      <div style={{
        padding: '12px 20px',
        backgroundColor: '#020617',
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
      }}>
        <div>
          <span style={{ color: '#64748b', fontSize: '0.75rem', letterSpacing: '0.05em' }}>DISTANCE</span>
          <div style={{ color: '#10b981', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace" }}>
            {totalMiles.toFixed(1)} mi
          </div>
        </div>
        <div>
          <span style={{ color: '#64748b', fontSize: '0.75rem', letterSpacing: '0.05em' }}>POINTS</span>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace" }}>
            {routePoints.length}
          </div>
        </div>
        {tripDuration && (
          <div>
            <span style={{ color: '#64748b', fontSize: '0.75rem', letterSpacing: '0.05em' }}>DURATION</span>
            <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace" }}>
              {formatDuration(tripDuration)}
            </div>
          </div>
        )}
        {avgSpeed && (
          <div>
            <span style={{ color: '#64748b', fontSize: '0.75rem', letterSpacing: '0.05em' }}>AVG SPEED</span>
            <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace" }}>
              {avgSpeed} km/h
            </div>
          </div>
        )}
        <div>
          <span style={{ color: '#64748b', fontSize: '0.75rem', letterSpacing: '0.05em' }}>STOPS</span>
          <div style={{ color: stops.length > 0 ? '#f59e0b' : '#e2e8f0', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace" }}>
            {stops.length}
          </div>
        </div>
      </div>

      {/* Map */}
      <div style={{ height: '400px', position: 'relative' }}>
        <MapContainer
          center={[startPoint.lat, startPoint.lng]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          whenReady={() => setMapReady(true)}
        >
          {/* Dark theme map tiles */}
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          />

          {/* Fit bounds to route */}
          <FitBounds points={routePoints} />

          {/* Route polyline */}
          <Polyline
            positions={routePath}
            pathOptions={{
              color: '#10b981',
              weight: 4,
              opacity: 0.8,
            }}
          />

          {/* Start marker */}
          <Marker position={[startPoint.lat, startPoint.lng]} icon={startIcon}>
            <Popup>
              <div style={{ fontWeight: 'bold', color: '#10b981' }}>START</div>
              <div>{formatTime(startPoint.timestamp)}</div>
            </Popup>
          </Marker>

          {/* End marker */}
          <Marker position={[endPoint.lat, endPoint.lng]} icon={endIcon}>
            <Popup>
              <div style={{ fontWeight: 'bold', color: '#ef4444' }}>END</div>
              <div>{formatTime(endPoint.timestamp)}</div>
            </Popup>
          </Marker>

          {/* Stop markers */}
          {stops.map((stop, idx) => (
            <Marker 
              key={idx} 
              position={[stop.lat, stop.lng]} 
              icon={stopIcon}
            >
              <Popup>
                <div style={{ fontWeight: 'bold', color: '#f59e0b' }}>STOP #{idx + 1}</div>
                <div>Duration: {formatDuration(stop.duration)}</div>
                <div>{formatTime(stop.timestamp)}</div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        gap: '24px',
        fontSize: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '50%' }} />
          <span style={{ color: '#94a3b8' }}>Start</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#ef4444', borderRadius: '50%' }} />
          <span style={{ color: '#94a3b8' }}>End</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#f59e0b', borderRadius: '50%' }} />
          <span style={{ color: '#94a3b8' }}>Stop (5+ min)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '24px', height: '4px', backgroundColor: '#10b981', borderRadius: '2px' }} />
          <span style={{ color: '#94a3b8' }}>Route path</span>
        </div>
      </div>

      {/* Timeline (if stops exist) */}
      {stops.length > 0 && (
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '12px' }}>
            STOP TIMELINE
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {stops.map((stop, idx) => (
              <div key={idx} style={{
                padding: '8px 12px',
                backgroundColor: '#020617',
                borderRadius: '4px',
                borderLeft: '3px solid #f59e0b',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <span style={{ color: '#e2e8f0', fontWeight: '500' }}>Stop #{idx + 1}</span>
                  <span style={{ color: '#64748b', marginLeft: '12px', fontSize: '0.875rem' }}>
                    {formatTime(stop.timestamp)}
                  </span>
                </div>
                <div style={{ color: '#f59e0b', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace" }}>
                  {formatDuration(stop.duration)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteMapView;
